import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { getAllBlogs } from "actions/action";
import { Base_URL, imageBaseURL } from "actions/Base_URL"; // Assuming image URL needs base path

const RecentActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedBlog, setSelectedBlog] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  // Handle body scroll lock
  useEffect(() => {
    if (selectedBlog) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [selectedBlog]);

  const fetchBlogs = async () => {
    try {
      const res = await getAllBlogs();
      if (res?.success && res.data) {
        // Transform blog data to match the UI structure
        const formattedBlogs = res.data.map((blog) => {
          let displayDate = blog.date;
          let displayMonth = blog.month;

          // Fallback to createdAt if custom date/month are missing
          if (!displayDate || !displayMonth) {
            const rawDate = blog.createdAt || blog.created_at || new Date();
            const blogDate = new Date(rawDate);

            if (!isNaN(blogDate.getTime())) {
              displayDate = blogDate.getDate();
              displayMonth = blogDate.toLocaleString('default', { month: 'short', year: 'numeric' });
            } else {
              displayDate = "N/A";
              displayMonth = "Unknown";
            }
          }

          return {
            date: displayDate,
            month: displayMonth,
            category: blog.type || "Other",
            author: blog.author,
            title: blog.title,
            description: blog.description || "",
            content: blog.content,
            // Use a placeholder if image_url is missing or empty
            image: blog.image_url
              ? `${imageBaseURL}${blog.image_url}`
              : "https://via.placeholder.com/400x300?text=NVCTI+Update",
            link: `/news/${blog.slug}`,
            id: blog.id
          };
        });
        setActivities(formattedBlogs.slice(0, 3)); // Show only latest 3
      }
    } catch (error) {
      console.error("Failed to fetch blogs", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="news-updates" className="py-24 bg-dark-secondary scroll-mt-24">
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center text-center mb-16">
          <div className="max-w-3xl mb-8">
            <h4 className="text-nvcti-orange font-bold uppercase tracking-widest text-sm mb-3">Innovation in Motion</h4>
            <h3 className="text-3xl md:text-5xl font-extrabold text-white mb-4">News & Updates</h3>
            <p className="text-gray-400 text-lg mb-6 leading-relaxed">
              Stay updated with our latest events and gallery on Instagram:
              <a href="https://www.instagram.com/nvcti.iitism/" target="_blank" rel="noopener noreferrer" className="text-accent-primary font-bold ml-2 hover:underline">
                @nvcti.iitism
              </a>
            </p>
            <div className="w-24 h-1.5 bg-nvcti-orange rounded-full mx-auto"></div>
          </div>
          <Link to="/blog" className="px-8 py-4 bg-dark-tertiary border border-white/10 text-white font-bold rounded-2xl hover:bg-white hover:text-dark-primary transition-all duration-300 shadow-xl shadow-black/20">
            View All Stories
          </Link>
        </div>

        {loading ? (
          <div className="text-white text-center">Loading updates...</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {activities.map((item, index) => (
              <div key={index} className="group bg-dark-tertiary rounded-3xl overflow-hidden shadow-2xl border border-white/5 hover:border-nvcti-orange/30 transition-all duration-300 flex flex-col h-full hover:-translate-y-2">

                {/* 1. Image Section (Top) */}
                <div className="relative h-64 overflow-hidden w-full shrink-0">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 brightness-90 group-hover:brightness-110"
                    loading="lazy"
                    onError={(e) => { e.target.src = "https://via.placeholder.com/400x300?text=No+Image"; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-tertiary via-transparent to-transparent opacity-60"></div>

                  {/* Date Badge Overlay */}
                  <div className="absolute top-4 left-4 bg-black border border-white/10 px-3 py-2 rounded-xl flex flex-col items-center min-w-[60px] shadow-xl">
                    <span className="text-nvcti-orange text-xl font-black leading-none mb-0.5">{item.date}</span>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-white">{item.month}</span>
                  </div>
                </div>

                {/* 2. Content Section (Bottom) */}
                <div className="flex flex-col flex-grow p-8">
                  {/* Meta Row: CATEGORY • MONTH YEAR */}
                  <div className="flex items-center gap-3 mb-4">
                    <span className="text-nvcti-orange text-xs font-black uppercase tracking-widest">
                      {item.category}
                    </span>
                    <span className="text-gray-600 text-[8px] font-black">●</span>
                    <span className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                      {item.month}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="text-2xl font-bold text-white mb-4 group-hover:text-nvcti-orange transition-colors line-clamp-2 leading-tight">
                    {item.title}
                  </h4>

                  {/* Description */}
                  <div className="mb-6">
                    <span className="text-gray-400 leading-relaxed text-sm block" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {item.description}
                    </span>
                  </div>

                  {/* Read More / Footer */}
                  <div className="mt-auto pt-4 border-t border-white/5 flex justify-between items-center w-full">
                    <button
                      onClick={() => setSelectedBlog(item)}
                      className="flex items-center text-white font-bold text-xs uppercase tracking-widest group/btn hover:text-nvcti-orange transition-colors"
                    >
                      Read More <span className="ml-2 text-nvcti-orange transform group-hover/btn:translate-x-1 transition-transform">&rarr;</span>
                    </button>
                    <span className="text-gray-600 text-[10px] font-bold uppercase tracking-widest">
                      {item.author}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Blog Detail Modal */}
      {selectedBlog && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 sm:p-6 bg-black/90 backdrop-blur-sm"
          onClick={() => setSelectedBlog(null)}
          style={{ zIndex: 99999 }}
        >
          <div
            className="bg-dark-tertiary w-full max-w-5xl max-h-[90vh] rounded-3xl border border-white/10 shadow-2xl flex flex-col relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header Area */}
            <div className="flex justify-between items-start p-6 md:p-8 border-b border-white/5 bg-dark-tertiary shrink-0 z-20">
              <div className="flex-1 pr-4">
                <div className="flex items-center gap-3 mb-3">
                  <span className="px-3 py-1 bg-nvcti-orange text-white text-xs font-black uppercase tracking-widest rounded-lg">
                    {selectedBlog.category}
                  </span>
                  <span className="text-gray-400 text-sm font-bold uppercase tracking-widest">
                    {selectedBlog.date} {selectedBlog.month}
                  </span>
                </div>
                <h2 className="text-2xl md:text-4xl font-black text-white leading-tight mb-2">
                  {selectedBlog.title}
                </h2>
                <div className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                  Post By {selectedBlog.author}
                </div>
              </div>
              <button
                onClick={() => setSelectedBlog(null)}
                className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center text-white hover:bg-nvcti-orange hover:text-white transition-all duration-300 shrink-0 shadow-lg"
              >
                <span className="text-3xl font-light leading-none relative -top-[1px]">&times;</span>
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="overflow-y-auto flex-grow custom-scrollbar bg-dark-secondary pb-8">
              {/* Hero Image inside content */}
              <div className="w-full h-64 md:h-[400px] relative">
                <img
                  src={selectedBlog.image}
                  alt={selectedBlog.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-dark-secondary via-dark-secondary/20 to-transparent"></div>
              </div>

              {/* Article Content */}
              <div className="px-6 md:px-12 -mt-12 relative z-10">
                <p className="text-xl md:text-2xl text-white font-extrabold leading-relaxed mb-8 border-l-4 border-nvcti-orange pl-6 italic opacity-90">
                  {selectedBlog.description}
                </p>
                <div className="text-gray-300 leading-relaxed text-lg whitespace-pre-wrap font-medium">
                  {selectedBlog.content}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/5 bg-dark-tertiary shrink-0 flex justify-end z-20">
              <button
                onClick={() => setSelectedBlog(null)}
                className="px-8 py-3 bg-white/10 text-white font-black uppercase tracking-widest text-sm rounded-xl hover:bg-nvcti-orange hover:text-white shadow-lg transition-all duration-300"
              >
                Close Article
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default RecentActivities;
